import { Card } from "./ui/card";
import { CHANNELS } from "../lib/channels";

export function PageHeader({ eyebrow, title, subtitle, children }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-3">
      <div>
        {eyebrow && <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{eyebrow}</div>}
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function KpiCard({ icon: Icon, label, value, hint, tone = "blue", testid }) {
  const tones = {
    blue: "bg-blue-50 text-blue-600", emerald: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600", rose: "bg-rose-50 text-rose-600", cyan: "bg-cyan-50 text-cyan-600",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <Card className="p-5 border border-slate-200 shadow-none card-lift" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">{label}</div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-2 truncate">{value}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
        {Icon && (
          <div className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ${tones[tone] || tones.blue}`}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </Card>
  );
}

export function ChannelBadge({ channel, className = "" }) {
  const c = CHANNELS[channel] || CHANNELS.belirtilmemis;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${c.cls} ${className}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c.color }} />
      {c.label}
    </span>
  );
}

export function StatusBadge({ map, value }) {
  const s = map[value] || { label: value, cls: "bg-slate-100 text-slate-700 border-slate-200" };
  return <span className={`status-pill border ${s.cls}`}>{s.label}</span>;
}

export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="text-center py-14 px-4">
      {Icon && <Icon className="h-10 w-10 text-slate-300 mx-auto mb-2" />}
      <div className="text-slate-600 font-medium">{title}</div>
      {hint && <div className="text-sm text-slate-400 mt-1 max-w-md mx-auto">{hint}</div>}
    </div>
  );
}

export function Panel({ title, subtitle, right, children, className = "" }) {
  return (
    <Card className={`p-5 border border-slate-200 shadow-none ${className}`}>
      {(title || right) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            {title && <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>}
            {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
          {right}
        </div>
      )}
      {children}
    </Card>
  );
}

// Basit yatay çubuk listesi (sayfa / kaynak / cihaz dağılımı)
export function BarList({ data, color = "#2563EB", empty = "Veri yok" }) {
  if (!data || data.length === 0) return <div className="text-sm text-slate-400 py-6 text-center">{empty}</div>;
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="space-y-2.5">
      {data.map((d) => (
        <div key={d.name}>
          <div className="flex items-center justify-between text-sm mb-1 gap-3">
            <span className="truncate text-slate-700" title={d.name}>{d.name}</span>
            <span className="font-mono font-semibold text-slate-900 shrink-0">{d.value}</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(d.value / max) * 100}%`, background: color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Silme / onay penceresi (popup)
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog";

export function ConfirmDialog({ open, onOpenChange, title, description, confirmText = "Sil", danger = true, onConfirm, testid }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testid || "confirm-dialog"}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Vazgeç</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className={danger ? "bg-rose-600 hover:bg-rose-700" : "bg-blue-600 hover:bg-blue-700"} data-testid="confirm-yes">
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Promise tabanlı popup onayı: const [ask, confirmEl] = useConfirm();  if (!(await ask("Silinsin mi?"))) return;
import { useState as _useState, useRef as _useRef } from "react";
export function useConfirm() {
  const [st, setSt] = _useState(null);
  const res = _useRef(null);
  const ask = (title, description) => new Promise((resolve) => { res.current = resolve; setSt({ title, description }); });
  const close = (v) => { setSt(null); res.current?.(v); };
  const el = (
    <ConfirmDialog open={!!st} onOpenChange={(o) => !o && close(false)} title={st?.title || ""} description={st?.description}
      onConfirm={() => close(true)} />
  );
  return [ask, el];
}
